import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { 
  CloudArrowUpIcon, 
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { syncService } from '../services/syncService';

interface SyncStatus {
  isOnline: boolean;
  isSyncing: boolean;
  lastSynced: number | null;
  pendingCount: number;
}

interface SyncStatusBannerProps {
  className?: string;
}

const SyncStatusBanner: React.FC<SyncStatusBannerProps> = ({ className = '' }) => {
  const [syncStatus, setSyncStatus] = useState<SyncStatus>({
    isOnline: navigator.onLine,
    isSyncing: false,
    lastSynced: null,
    pendingCount: 0,
  });
  const [isVisible, setIsVisible] = useState(false);

  // Format timestamp for display
  const formatTimestamp = (timestamp: number): string => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMinutes < 1) {
      return 'Just now';
    } else if (diffMinutes < 60) {
      return `${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''} ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    } else {
      return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    }
  };

  // Update sync status
  const updateSyncStatus = async () => {
    try {
      const status = await syncService.getSyncStatus();
      setSyncStatus(status);
      
      // Show banner if there are pending items or if offline
      setIsVisible(status.pendingCount > 0 || !status.isOnline);
    } catch (error) {
      console.error('Failed to get sync status:', error);
    }
  };

  // Manual sync
  const handleManualSync = async () => {
    if (!syncStatus.isOnline) {
      toast.error('Cannot sync while offline');
      return;
    }

    try {
      await syncService.manualSync();
      toast.success('Sync completed successfully');
      await updateSyncStatus();
    } catch (error) {
      toast.error('Sync failed. Please try again.');
      console.error('Manual sync failed:', error);
    }
  };

  // Setup periodic status updates
  useEffect(() => {
    updateSyncStatus();
    
    const interval = setInterval(updateSyncStatus, 5000);
    
    return () => clearInterval(interval);
  }, []);

  // Don't render if not visible
  if (!isVisible) {
    return null;
  }

  return (
    <div className={`fixed top-0 left-0 right-0 z-50 ${className}`}>
      <div className={`
        px-4 py-3 text-sm font-medium
        ${syncStatus.isOnline 
          ? syncStatus.pendingCount > 0 
            ? 'bg-yellow-50 border-b border-yellow-200 text-yellow-800' 
            : 'bg-green-50 border-b border-green-200 text-green-800'
          : 'bg-red-50 border-b border-red-200 text-red-800'
        }
      `}>
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center space-x-2 rtl:space-x-reverse">
            {/* Status Icon */}
            {!syncStatus.isOnline ? (
              <ExclamationTriangleIcon className="h-5 w-5 text-red-600" />
            ) : syncStatus.isSyncing ? (
              <ArrowPathIcon className="h-5 w-5 text-yellow-600 animate-spin" />
            ) : syncStatus.pendingCount > 0 ? (
              <CloudArrowUpIcon className="h-5 w-5 text-yellow-600" />
            ) : (
              <CheckCircleIcon className="h-5 w-5 text-green-600" />
            )}

            {/* Status Text */}
            <span>
              {!syncStatus.isOnline ? (
                'You are offline. Changes will be synced when you reconnect.'
              ) : syncStatus.isSyncing ? (
                'Syncing changes...'
              ) : syncStatus.pendingCount > 0 ? (
                `${syncStatus.pendingCount} change${syncStatus.pendingCount !== 1 ? 's' : ''} pending sync`
              ) : (
                'All changes synced'
              )}
            </span>

            {/* Last Synced */}
            {syncStatus.lastSynced && syncStatus.isOnline && (
              <span className="text-xs opacity-75">
                Last synced: {formatTimestamp(syncStatus.lastSynced)}
              </span>
            )}
          </div>

          {/* Action Button */}
          {syncStatus.isOnline && syncStatus.pendingCount > 0 && !syncStatus.isSyncing && (
            <button
              onClick={handleManualSync}
              className="flex items-center space-x-1 rtl:space-x-reverse px-3 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-md hover:bg-yellow-200 transition-colors"
            >
              <CloudArrowUpIcon className="h-4 w-4" />
              <span>Sync Now</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SyncStatusBanner; 